'use client';

import { nowAsUTC, toUTCDate } from '@/components/datePicker/utils/date';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ClassNameProp } from '@/types/react';
import { utc, UTCDate } from '@date-fns/utc';
import { endOfMonth } from 'date-fns/endOfMonth';
import { startOfMonth } from 'date-fns/startOfMonth';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import * as React from 'react';
import { useMemo, useState } from 'react';

export type Month = { number: number; name: string; yearOffset: number };

const MONTHS: Month[][] = [
  [
    { number: 0, name: 'Jan', yearOffset: 0 },
    { number: 1, name: 'Feb', yearOffset: 0 },
    { number: 2, name: 'Mar', yearOffset: 0 },
    { number: 3, name: 'Apr', yearOffset: 0 },
    { number: 0, name: 'Jan', yearOffset: 1 },
    { number: 1, name: 'Feb', yearOffset: 1 },
    { number: 2, name: 'Mar', yearOffset: 1 },
    { number: 3, name: 'Apr', yearOffset: 1 },
  ],
  [
    { number: 4, name: 'May', yearOffset: 0 },
    { number: 5, name: 'Jun', yearOffset: 0 },
    { number: 6, name: 'Jul', yearOffset: 0 },
    { number: 7, name: 'Aug', yearOffset: 0 },
    { number: 4, name: 'May', yearOffset: 1 },
    { number: 5, name: 'Jun', yearOffset: 1 },
    { number: 6, name: 'Jul', yearOffset: 1 },
    { number: 7, name: 'Aug', yearOffset: 1 },
  ],
  [
    { number: 8, name: 'Sep', yearOffset: 0 },
    { number: 9, name: 'Oct', yearOffset: 0 },
    { number: 10, name: 'Nov', yearOffset: 0 },
    { number: 11, name: 'Dec', yearOffset: 0 },
    { number: 8, name: 'Sep', yearOffset: 1 },
    { number: 9, name: 'Oct', yearOffset: 1 },
    { number: 10, name: 'Nov', yearOffset: 1 },
    { number: 11, name: 'Dec', yearOffset: 1 },
  ],
];

type MonthRangeProps = {
  selectedMonthRange?: { from: Date; to?: Date };
  onStartMonthSelect?: (date: Date) => void;
  onMonthRangeSelect?: ({ from, to }: { from: Date; to: Date }) => void;
  onYearForward?: () => void;
  onYearBackward?: () => void;
  callbacks?: { yearLabel?: (year: number) => string; monthLabel?: (month: Month) => string };
  minDate?: Date | null;
  maxDate?: Date | null;
};

const MonthRangePicker: React.FC<MonthRangeProps & ClassNameProp> = ({ className, ...props }) => {
  return (
    <div className={cn('flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4', className)}>
      <MonthRangeCal {...props} />
    </div>
  );
};

const MonthRangeCal: React.FC<MonthRangeProps> = ({
  selectedMonthRange,
  onMonthRangeSelect,
  onStartMonthSelect,
  callbacks,
  minDate,
  maxDate,
  onYearBackward,
  onYearForward,
}) => {
  const [startDate, setStartDate] = useState(
    toUTCDate(selectedMonthRange?.from) ?? startOfMonth(nowAsUTC, { in: utc }),
  );

  const maxDateUTC = useMemo(() => toUTCDate(maxDate), [maxDate]);
  const minDateUTC = useMemo(() => {
    const utcDate = toUTCDate(minDate);
    const isMinDateLargerThanMax = utcDate && maxDateUTC && utcDate > maxDateUTC;
    if (isMinDateLargerThanMax) return maxDateUTC;
    return utcDate;
  }, [minDate, maxDateUTC]);

  const initialEndDate = useMemo(() => {
    const isEndAfterMax = maxDateUTC
      ? selectedMonthRange?.to?.getFullYear() === maxDateUTC.getFullYear() &&
        selectedMonthRange.to.getMonth() > maxDateUTC.getMonth()
      : false;
    return isEndAfterMax ? maxDateUTC : toUTCDate(selectedMonthRange?.to);
  }, [maxDateUTC, selectedMonthRange]);

  const [endDate, setEndDate] = useState<UTCDate | undefined | null>(initialEndDate);
  const [rangePending, setRangePending] = useState(false);
  const [endLocked, setEndLocked] = useState(true);
  const [menuYear, setMenuYear] = useState(startDate.getFullYear());

  const handleMonthClick = (clickedDate: UTCDate, isBeforeStart: boolean) => {
    if (rangePending) {
      onEndMonthClick(clickedDate, isBeforeStart);
    } else {
      onStartMonthClick(clickedDate);
    }
  };

  const onEndMonthClick = (clickedDate: UTCDate, isBeforeStart: boolean) => {
    if (isBeforeStart) {
      setRangePending(true);
      setEndLocked(false);
      setStartDate(clickedDate);
      setEndDate(undefined);
      if (onStartMonthSelect) onStartMonthSelect(clickedDate);
    } else {
      setEndDate(endOfMonth(clickedDate, { in: utc }));
      setRangePending(false);
      setEndLocked(true);
      if (onMonthRangeSelect) onMonthRangeSelect({ from: startDate, to: endOfMonth(clickedDate) });
    }
  };

  const onStartMonthClick = (clickedDate: UTCDate) => {
    setStartDate(clickedDate);
    setRangePending(true);
    setEndLocked(false);
    setEndDate(undefined);
    if (onStartMonthSelect !== undefined) onStartMonthSelect(clickedDate);
  };

  return (
    <div className="flex w-full gap-4">
      <div className="w-full space-y-4 sm:min-w-[400px]">
        <div className="relative flex justify-evenly">
          <div className="flex flex-1 items-center justify-between">
            <ChangeYearButton
              onClick={() => {
                setMenuYear(menuYear - 1);
                if (onYearBackward) onYearBackward();
              }}
            >
              <ArrowLeft className="size-4 opacity-50" />
            </ChangeYearButton>
            <YearLabel labelFn={callbacks?.yearLabel} year={menuYear} />
            <div className="size-7" aria-hidden />
          </div>
          <div className="flex flex-1 items-center justify-between">
            <div className="size-7" aria-hidden />
            <YearLabel labelFn={callbacks?.yearLabel} year={menuYear + 1} />
            <ChangeYearButton
              onClick={() => {
                setMenuYear(menuYear + 1);
                if (onYearForward) onYearForward();
              }}
            >
              <ArrowRight className="size-4 opacity-50" />
            </ChangeYearButton>
          </div>
        </div>
        <table className="w-full border-collapse space-y-1">
          <tbody>
            {MONTHS.map((monthRow, rowIndex) => {
              return (
                <tr key={'row-' + rowIndex} className="mt-2 flex w-full">
                  {monthRow.map((month, columnIndex) => (
                    <MonthCell
                      key={month.number + '-' + month.yearOffset}
                      month={month}
                      columnIndex={columnIndex}
                      menuYear={menuYear}
                      startDate={startDate}
                      endDate={endDate}
                      rangePending={rangePending}
                      endLocked={endLocked}
                      minDate={minDateUTC}
                      maxDate={maxDateUTC}
                      onMonthClick={handleMonthClick}
                    >
                      {callbacks?.monthLabel ? callbacks.monthLabel(month) : month.name}
                    </MonthCell>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const YearLabel: React.FC<{ year: number; labelFn?: (year: number) => React.ReactNode }> = ({
  year,
  labelFn,
}) => <div className="text-sm font-medium">{labelFn ? labelFn(year) : year}</div>;

const ChangeYearButton: React.FC<React.ComponentProps<'button'>> = ({ className, ...props }) => (
  <button
    className={cn(
      buttonVariants({ variant: 'outline' }),
      'inline-flex size-8 cursor-pointer items-center justify-center p-0',
      className,
    )}
    {...props}
  />
);

const MonthCell: React.FC<
  React.PropsWithChildren<{
    month: Month;
    columnIndex: number;
    menuYear: number;
    startDate: UTCDate;
    endDate: UTCDate | undefined | null;
    rangePending: boolean;
    endLocked: boolean;
    minDate?: UTCDate | null;
    maxDate?: UTCDate | null;
    onMonthHover?: (date: UTCDate) => void;
    onMonthClick?: (date: UTCDate, isBeforeStart: boolean) => void;
  }>
> = (props) => {
  const { startDate, endDate, month, menuYear, rangePending, endLocked } = props;
  const { maxDate, minDate, onMonthClick, onMonthHover, columnIndex, children } = props;

  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth();
  const endYear = endDate?.getFullYear();
  const endMonth = endDate?.getMonth();
  const monthYear = menuYear + month.yearOffset;

  const isInRange =
    (monthYear > startYear || (monthYear === startYear && month.number > startMonth)) &&
    ((endYear !== undefined && monthYear < endYear) ||
      (endMonth !== undefined && monthYear === endYear && month.number < endMonth)) &&
    (rangePending || endLocked);

  const isStartCell =
    monthYear === startYear && month.number === startMonth && (rangePending || endLocked);

  const isEndCell =
    monthYear === endYear &&
    month.number === endMonth &&
    (rangePending || endLocked) &&
    monthYear >= startYear;

  const isAfterMax = maxDate
    ? monthYear > maxDate.getFullYear() ||
      (monthYear === maxDate.getFullYear() && month.number > maxDate.getMonth())
    : false;

  const isBeforeMin = minDate
    ? monthYear < minDate.getFullYear() ||
      (monthYear === minDate.getFullYear() && month.number < minDate.getMonth())
    : false;

  const isBeforeStart =
    monthYear < startYear || (monthYear === startYear && month.number < startMonth);

  const clickedDate = new UTCDate(monthYear, month.number);

  return (
    <MonthCellWrapper
      disabled={isAfterMax || isBeforeMin}
      isInRange={isInRange}
      isStartCell={isStartCell}
      isEndCell={isEndCell}
      isColumnBreak={columnIndex === 3}
      isColumnStart={columnIndex === 4}
      onMouseEnter={() => {
        if (rangePending && !endLocked) onMonthHover?.(clickedDate);
      }}
    >
      <button
        disabled={isAfterMax || isBeforeMin}
        onClick={() => onMonthClick?.(clickedDate, isBeforeStart)}
        className={cn(
          buttonVariants({ variant: 'ghost' }),
          'size-full rounded-none p-0 font-normal hover:rounded-md disabled:text-muted-foreground aria-selected:opacity-100',
        )}
      >
        {children}
      </button>
    </MonthCellWrapper>
  );
};

const MonthCellWrapper: React.FC<
  React.PropsWithChildren<{
    disabled: boolean;
    isInRange: boolean;
    isStartCell: boolean;
    isEndCell: boolean;
    isColumnBreak: boolean;
    isColumnStart: boolean;
    onMouseEnter: () => void;
  }>
> = (props) => {
  const { disabled, isInRange, isStartCell, isEndCell, isColumnBreak, isColumnStart, ...rest } =
    props;
  return (
    <td
      className={cn(
        'relative h-10 w-1/4 overflow-hidden p-0 text-center text-sm focus-within:relative focus-within:z-20 has-aria-[selected]:bg-accent first:has-aria-[selected]:rounded-l-md last:has-aria-[selected]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
        !disabled && isInRange && 'bg-[#ECF3F1] text-accent-foreground [&>*:hover]:bg-inherit',
        !disabled &&
          isStartCell &&
          'rounded-l-md bg-[#B2D3C9] text-primary [&>*:hover]:rounded-none [&>*:hover]:bg-inherit',
        !disabled &&
          isEndCell &&
          'rounded-r-md bg-[#B2D3C9] text-primary [&>*:hover]:rounded-none [&>*:hover]:bg-inherit',
        isColumnBreak && 'mr-2',
        isColumnStart && 'ml-2',
      )}
      {...rest}
    />
  );
};

MonthRangePicker.displayName = 'MonthRangePicker';

export { MonthRangePicker };
